import { createSelector } from '@reduxjs/toolkit';
import { isEqual } from 'lodash-es';

import { Account } from '@/shared/api/protocol-ts/api/billing_new/dto_account_new_pb';
import {
  DiagnocoinBatch,
  DiagnocoinPriceList,
  DiagnocoinWallet,
  ReferralRewards,
} from '@/shared/api/protocol-ts/api/billing_new/dto_diagnocoin_new_pb';
import { Order } from '@/shared/api/protocol-ts/api/billing_new/dto_order_new_pb';
import {
  ProductState,
  ProductStateStatus,
} from '@/shared/api/protocol-ts/api/billing_new/dto_product_new_pb';
import { ServiceType } from '@/shared/api/protocol-ts/api/billing_new/dto_services_new_pb';
import { StudyType } from '@/shared/api/protocol-ts/model/dto_study_pb';
import { LoadingStateType } from '@/shared/config';

import { RootState } from '@/app/model/store';

import {
  BillingZoneParams,
  DiagnocoinFundingByOrderReportType,
  DiagnocoinFundingContext,
  EXPIRY_HINT_MONTHS,
  ExpiringSoonDiagnocoins,
  ORDER_REPORT_TYPE_SERVICE_TYPES,
  STUDY_TO_SERVICES_MAP,
  StudyToServiceMap,
  SUBSCRIPTION_EFFECT_TYPES,
} from '../config';
import {
  getDiagnocoinFunding,
  getServicesFromSubscriptionList,
  isBillingZone,
  isCoinSpendableSubscription,
  isProductActive,
} from '../lib';

export const selectAccount = (state: RootState): Account =>
  state.billing.account;

export const selectInventory = (state: RootState): ProductState[] =>
  state.billing.inventory;

export const selectInvoices = (state: RootState): Order[] =>
  state.billing.invoices;

export const selectCurrentSubscription = createSelector(
  selectInventory,
  (inventory) =>
    inventory
      ?.filter(
        (inventoryItem) =>
          inventoryItem.Model?.Kind.case === 'Subscription' &&
          isProductActive(inventoryItem),
      )
      ?.at(0),
);

export const selectScheduledSubscription = createSelector(
  selectInventory,
  (inventory) =>
    inventory?.find(
      (inventoryItem) =>
        inventoryItem.Model?.Kind.case === 'Subscription' &&
        inventoryItem.Status === ProductStateStatus.ProductStateStatusActive &&
        inventoryItem.Start !== undefined &&
        inventoryItem.Start.toDate() > new Date(),
    ),
);

export const selectSubscriptionLifeTime = (state: RootState): number =>
  state.billing.subscriptionLifeTime;

export const selectBillingZoneAndCountry = createSelector(
  selectAccount,
  (account): BillingZoneParams => {
    const billingZone = account?.BillingZones?.at(0);

    return {
      billingZone: isBillingZone(billingZone) ? billingZone : undefined,
      country: account?.Country,
    };
  },
);

export const selectCurrentPackages = createSelector(
  selectInventory,
  (inventory) =>
    inventory.filter(
      (inventoryItem) =>
        inventoryItem.Status === ProductStateStatus.ProductStateStatusActive &&
        inventoryItem?.Model?.Kind.case === 'Package',
    ),
);

export const selectBilledInvoices = createSelector(selectInvoices, (invoices) =>
  invoices.filter(
    (invoice) =>
      (invoice.Kind.case === 'Billed' &&
        invoice.Effect?.Effect?.case === 'SubscriptionAdd') ||
      invoice.Effect?.Effect?.case === 'SubscriptionRenew' ||
      invoice.Effect?.Effect?.case === 'SubscriptionSetAutoRenewal' ||
      invoice.Effect?.Effect?.case === 'PackageAdd',
  ),
);

export const selectActiveProducts = createSelector(
  selectInventory,
  (inventory) => {
    if (inventory.length === 0) return [] as ProductState[];

    return inventory.filter(isProductActive);
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
    },
  },
);

export const selectAvailableProducts = (state: RootState) =>
  state.billing.availableProducts;

export const selectAvailableSubscriptions = createSelector(
  selectAvailableProducts,
  (availableProducts) =>
    availableProducts.filter(
      (product) =>
        product.Kind.case === 'Subscription' && !product.AvailableForAdminsOnly,
    ),
);

export const selectLoading = (state: RootState) => state.billing.loading;

export const selectIsBillingLoaded = createSelector(
  selectLoading,
  (loading) => loading === 'succeeded' || loading === 'failed',
);

export const selectActiveServicesList = createSelector(
  selectActiveProducts,
  (productList) => {
    const services = getServicesFromSubscriptionList(productList);

    const activeServicesList = services.reduce(
      (activeServices, service) => {
        service.Types.forEach((serviceType) => {
          const isEnabler = service.Kind.case === 'Enabler';

          const leftover =
            (service.Kind.case === 'Countable' &&
              Number(service.Kind.value.Leftover)) ||
            0;

          activeServices[serviceType] = isEnabler
            ? 1
            : (activeServices[serviceType] ?? 0) + leftover;
        });

        return activeServices;
      },
      {} as Record<ServiceType, number>,
    );

    return activeServicesList;
  },
);

export const selectIsReportOrderAvailableByStudyType = (
  studyType: StudyType | undefined,
) =>
  createSelector(
    selectActiveServicesList,
    (services) =>
      !services[STUDY_TO_SERVICES_MAP[studyType as keyof StudyToServiceMap]],
  );

export const selectIsServiceActiveByType = (serviceType: ServiceType) =>
  createSelector(selectActiveServicesList, (services) =>
    Boolean(services[serviceType]),
  );

export const selectActiveServiceValuesByTypes = (
  serviceTypes: readonly ServiceType[],
) =>
  createSelector(selectActiveServicesList, (services) =>
    serviceTypes.reduce(
      (activeServices, serviceType) => {
        if (services[serviceType] !== undefined) {
          activeServices[serviceType] = services[serviceType];
        }

        return activeServices;
      },
      {} as Partial<Record<ServiceType, number>>,
    ),
  );

export const selectServicesByType = (serviceType: ServiceType) =>
  createSelector(selectActiveProducts, (activeProducts) => {
    const services = getServicesFromSubscriptionList(activeProducts);

    return services.filter((service) => service.Types.includes(serviceType));
  });

export const selectUnPaidOrder = createSelector(
  selectBilledInvoices,
  (invoices) =>
    invoices.find(
      (invoice) =>
        invoice.Kind.case === 'Billed' &&
        !invoice.Kind.value.Paid &&
        !invoice.Kind.value.Overdue,
    ),
);

export const selectActiveSubscriptions = createSelector(
  selectInventory,
  (inventory) =>
    inventory?.filter(
      (inventoryItem) =>
        inventoryItem.Model?.Kind.case === 'Subscription' &&
        inventoryItem.Status === ProductStateStatus.ProductStateStatusActive,
    ),
);

export const selectHasActiveSubscriptionOrPackages = createSelector(
  selectActiveSubscriptions,
  selectCurrentPackages,
  (activeSubscriptions, currentPackages) =>
    activeSubscriptions.length > 0 || currentPackages.length > 0,
);

export const selectUnpaidSubscriptionOrder = createSelector(
  selectUnPaidOrder,
  (unpaidOrder) => {
    if (!unpaidOrder) return null;

    const effectCase = unpaidOrder.Effect?.Effect?.case;
    const isSubscriptionEffect =
      effectCase === SUBSCRIPTION_EFFECT_TYPES.ADD ||
      effectCase === SUBSCRIPTION_EFFECT_TYPES.RENEW ||
      effectCase === SUBSCRIPTION_EFFECT_TYPES.AUTO_RENEWAL;

    return isSubscriptionEffect ? unpaidOrder : null;
  },
);

export const selectPaymentReminderData = createSelector(
  selectCurrentSubscription,
  selectActiveSubscriptions,
  selectUnpaidSubscriptionOrder,
  (currentSubscription, activeSubscriptions, unpaidSubscriptionOrder) => ({
    currentSubscription,
    activeSubscriptions,
    unpaidSubscriptionOrder,
    hasUnpaidSubscription: Boolean(unpaidSubscriptionOrder),
    hasMultipleActiveSubscriptions: activeSubscriptions.length >= 2,
    nextSubscriptionStart: activeSubscriptions.at(-1)?.Start?.toDate() ?? null,
    currentSubscriptionEnd: currentSubscription?.End?.toDate() ?? null,
  }),
);

export const selectDiagnocoinWallet = (state: RootState): DiagnocoinWallet =>
  state.billing.diagnocoinWallet;

export const selectDiagnocoinBatches = (state: RootState): DiagnocoinBatch[] =>
  state.billing.diagnocoinBatches;

export const selectDiagnocoinPrices = (state: RootState): DiagnocoinPriceList =>
  state.billing.diagnocoinPrices;

export const selectDiagnocoinLoading = (state: RootState): LoadingStateType =>
  state.billing.diagnocoinLoading;

const selectReferralRewardsMessage = (
  state: RootState,
): ReferralRewards | null => state.billing.referralRewards;

export const selectReferralRewards = createSelector(
  selectReferralRewardsMessage,
  (rewards) =>
    rewards && {
      referrerCoins: Number(rewards.ReferrerCoins),
    },
);

export const selectIsDiagnocoinReady = createSelector(
  selectDiagnocoinLoading,
  (loading) => loading === 'succeeded',
);

export const selectDiagnocoinAvailable = createSelector(
  selectDiagnocoinWallet,
  (wallet) => Number(wallet?.Available ?? 0),
);

export const selectDiagnocoinReserved = createSelector(
  selectDiagnocoinWallet,
  (wallet) => Number(wallet?.Reserved ?? 0),
);

export const selectDiagnocoinPriceByServiceType = createSelector(
  selectDiagnocoinPrices,
  (priceList) =>
    Object.fromEntries(
      (priceList?.Prices ?? []).map((price) => [
        price.ServiceType,
        Number(price.Coins),
      ]),
    ) as Partial<Record<ServiceType, number>>,
);

export const selectActiveDiagnocoinBatches = createSelector(
  selectDiagnocoinBatches,
  (batches) =>
    batches
      .filter((batch) => Number(batch.Remaining) > 0)
      .sort(
        (a, b) =>
          (a.PurchasedAt?.toDate().getTime() ?? 0) -
          (b.PurchasedAt?.toDate().getTime() ?? 0),
      ),
);

export const selectExpiringSoonDiagnocoins = createSelector(
  selectActiveDiagnocoinBatches,
  (batches): ExpiringSoonDiagnocoins | null => {
    const now = new Date();
    const threshold = new Date(
      now.getFullYear(),
      now.getMonth() + EXPIRY_HINT_MONTHS,
      now.getDate(),
    );

    const expiringSoon = batches.filter((batch) => {
      const expiresAt = batch.ExpiresAt?.toDate();

      return expiresAt !== undefined && expiresAt <= threshold;
    });

    if (expiringSoon.length === 0) {
      return null;
    }

    return {
      coins: expiringSoon.reduce(
        (total, batch) => total + Number(batch.Remaining),
        0,
      ),
      expiresAt: expiringSoon[0].ExpiresAt?.toDate() ?? threshold,
      threshold,
    };
  },
);

export const selectHasCoinSpendableSubscription = createSelector(
  selectInventory,
  (inventory) => inventory.some(isCoinSpendableSubscription),
);

export const selectDiagnocoinFundingContext = createSelector(
  selectActiveServicesList,
  selectDiagnocoinPriceByServiceType,
  selectDiagnocoinAvailable,
  selectHasCoinSpendableSubscription,
  selectIsDiagnocoinReady,
  (
    services,
    prices,
    available,
    hasCoinSpendableSubscription,
    isReady,
  ): DiagnocoinFundingContext => ({
    services,
    prices,
    available,
    hasCoinSpendableSubscription,
    isReady,
  }),
);

export const selectDiagnocoinFundingByOrderReportType = createSelector(
  selectDiagnocoinFundingContext,
  (context): DiagnocoinFundingByOrderReportType =>
    Object.fromEntries(
      Object.entries(ORDER_REPORT_TYPE_SERVICE_TYPES).map(
        ([reportType, serviceTypes]) => [
          reportType,
          getDiagnocoinFunding(serviceTypes, context),
        ],
      ),
    ) as DiagnocoinFundingByOrderReportType,
);

export const makeSelectDiagnocoinFundingForServiceTypes = (
  serviceTypes: readonly ServiceType[],
) =>
  createSelector(selectDiagnocoinFundingContext, (context) =>
    getDiagnocoinFunding(serviceTypes, context),
  );

export const makeSelectDiagnocoinFundingByServiceType = (
  serviceType: ServiceType | undefined,
) =>
  makeSelectDiagnocoinFundingForServiceTypes(
    serviceType === undefined ? [] : [serviceType],
  );
